import { type AnalysisRun, isRunFinished } from '../model.js';
import { runFailureReason } from './run-failure.js';

/**
 * Which run a video's review opens on, and what it says about the runs after it.
 *
 * «La última completa»: the latest run that succeeded, whatever launched it — an upload, a
 * re-analysis, a group or a folder, a replay or an automatic retry. The product owner's rule
 * (2026-09-24) is «solo la última … para todo», and a group re-run never becomes the run a video
 * shows, so opening on that one would show an older analysis than the latest one the video has.
 * Upstream's pointer is left alone: Beon still reads it, and only what the back-office opens on
 * changes.
 *
 * A failed or running run is never opened by default — it has nothing to show — but it is not
 * hidden either: when the latest run is one of those, the review says so above the one it opened.
 */

/**
 * How long after a failure upstream still retries it by itself.
 *
 * Its sweep looks back one hour, so a failure older than that with no retry behind it will not get
 * one. Saying «se reintentará sola» about it would have somebody waiting for a run that never comes.
 */
export const AUTO_RETRY_WINDOW_MS = 60 * 60 * 1000;

/**
 * The runs newest first, by when they were queued.
 *
 * Ordered here rather than trusted from the list, like the executions of a group; a tie keeps the
 * list's order, and a run with no instant goes last.
 */
export function runsNewestFirst(runs: readonly AnalysisRun[] | null | undefined): AnalysisRun[] {
  return [...(runs ?? [])]
    .map((run, index) => ({ run, index }))
    .sort((a, b) => {
      const left = a.run.enqueuedAt?.getTime() ?? Number.NEGATIVE_INFINITY;
      const right = b.run.enqueuedAt?.getTime() ?? Number.NEGATIVE_INFINITY;

      return right - left || a.index - b.index;
    })
    .map(({ run }) => run);
}

/** The latest run that succeeded, of any origin. Null when none did. */
export function latestCompleteRun(
  runs: readonly AnalysisRun[] | null | undefined,
): AnalysisRun | null {
  return runsNewestFirst(runs).find((run) => run.status === 'SUCCEEDED') ?? null;
}

/**
 * The run a review opens on when the link names none.
 *
 * `undefined` when no run of the ledger succeeded — a video older than the ledger, or one whose
 * every run failed. Then it is upstream's own pointer, which is all such a video has.
 */
export function defaultRunId(runs: readonly AnalysisRun[] | null | undefined): string | undefined {
  return latestCompleteRun(runs)?.id;
}

/**
 * What follows a failed run: upstream retrying it by itself, somebody relaunching it, or somebody
 * fixing its configuration first — a configuration error fails the same way on every relaunch.
 */
export type FailedFollowUp = 'auto_retry' | 'manual' | 'fix_configuration';

/**
 * Whether upstream will retry a failed run by itself.
 *
 * Its rule, mirrored: not a configuration error — the engine refuses those before paying for
 * anything, the same way every time —, a video's run (interviews and CVs are never retried), not
 * itself a retry — two failures in a row stop it —, not one somebody asked to cancel, with the job
 * it would replay stored, and recent enough for the sweep to still see it. Nor a failure upstream
 * never retries although no configuration caused it: a video whose transcription was not done.
 */
export function failedFollowUp(
  run: Pick<
    AnalysisRun,
    'sourceType' | 'trigger' | 'cancellation' | 'hasStoredJob' | 'finishedAt' | 'error'
  >,
  now: number,
): FailedFollowUp {
  const reason = runFailureReason(run.error);

  if (reason?.kind === 'configuration') return 'fix_configuration';
  if (reason?.retryable === false) return 'manual';
  if (run.sourceType !== 'manual_analysis') return 'manual';
  if (run.trigger === 'auto_retry') return 'manual';
  if (run.cancellation) return 'manual';
  if (!run.hasStoredJob) return 'manual';
  if (!run.finishedAt || now - run.finishedAt.getTime() > AUTO_RETRY_WINDOW_MS) return 'manual';

  return 'auto_retry';
}

/**
 * A run newer than the one on screen that has nothing to show yet, or will not have.
 *
 * - `running`: not finished — in flight, or `PAUSED` with its execution; `isRetry` when upstream
 *   launched it after a failure.
 * - `failed`: it failed, with what follows — a retry on its way, or a person relaunching it.
 *   `retryFailed` when it was itself the retry, so the video failed twice in a row.
 */
export type NewerRunNotice =
  | { kind: 'running'; run: AnalysisRun; isRetry: boolean }
  | { kind: 'failed'; run: AnalysisRun; followUp: FailedFollowUp; retryFailed: boolean };

/**
 * What the review says about the latest run when it is not the one on screen.
 *
 * Only about the latest: a failure followed by a success is history, and the review is already on
 * the success. A newer run that succeeded — somebody opened an older one on purpose — or was
 * cancelled says nothing: neither is something to wait for or to act on.
 */
export function newerRunNotice(
  runs: readonly AnalysisRun[] | null | undefined,
  shownRunId: string | null | undefined,
  now: number,
): NewerRunNotice | null {
  const latest = runsNewestFirst(runs)[0];

  if (!latest || latest.id === shownRunId) return null;

  if (!isRunFinished(latest.status)) {
    return { kind: 'running', run: latest, isRetry: latest.trigger === 'auto_retry' };
  }

  if (latest.status === 'FAILED') {
    return {
      kind: 'failed',
      run: latest,
      followUp: failedFollowUp(latest, now),
      retryFailed: latest.trigger === 'auto_retry',
    };
  }

  return null;
}

/** What the notice says, in Spanish. */
export interface NoticeCopy {
  title: string;
  body: string;
  /**
   * Why the run failed, in upstream's words: English, and what an operator copies into a ticket.
   * Null for a running run and for a failure that recorded no error.
   */
  detail: string | null;
  /** Whether it offers to relaunch the video: only when nothing else is going to. */
  offersRelaunch: boolean;
}

const OLDER = 'Lo que ves es una ejecución anterior.';

/**
 * What happens next to a failed run, as one sentence.
 *
 * Said wherever a failure is, because «falló» alone sends people to relaunch a run that is about to
 * be retried — paying for it twice — or to relaunch one whose configuration will make it fail again.
 */
export function followUpSentence(followUp: FailedFollowUp, retryFailed: boolean): string {
  if (followUp === 'fix_configuration') {
    return 'No se reintentará sola: con esta configuración fallaría igual. Corrige la configuración y vuelve a lanzar el análisis.';
  }
  if (followUp === 'auto_retry') return 'Virtual Profiler la reintentará sola en unos minutos.';
  if (retryFailed) {
    return 'También falló su reintento automático, así que no se volverá a intentar sola: relánzala cuando haga falta.';
  }

  return 'No se reintentará sola: relánzala cuando haga falta.';
}

function spanishInstant(when: Date): string {
  return when.toLocaleString('es-ES', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * The notice in words.
 *
 * Every sentence ends by saying that the review is on an older run, because that is the fact a
 * reader would otherwise get wrong. A failure starts with which step failed, when this build has a
 * name for it, and says what happens next (`followUpSentence`); upstream's own words for why go in
 * `detail`.
 */
export function noticeCopy(
  notice: NewerRunNotice,
  formatWhen: (when: Date) => string = spanishInstant,
): NoticeCopy {
  if (notice.kind === 'running') {
    // Its execution was paused before it started: nothing moves until somebody resumes it.
    if (notice.run.status === 'PAUSED') {
      return {
        title: 'Hay una ejecución más reciente en pausa',
        body: `Cuando se reanude y termine bien, será la última completa. ${OLDER}`,
        detail: null,
        offersRelaunch: false,
      };
    }

    return notice.isRetry
      ? {
          title: 'Reintento automático en marcha',
          body: `La última ejecución falló y Virtual Profiler la está repitiendo sola. ${OLDER}`,
          detail: null,
          offersRelaunch: false,
        }
      : {
          title: 'Hay una ejecución más reciente en marcha',
          body: `Cuando termine bien, será la última completa. ${OLDER}`,
          detail: null,
          offersRelaunch: false,
        };
  }

  const when = notice.run.finishedAt ? ` el ${formatWhen(notice.run.finishedAt)}` : '';
  const reason = runFailureReason(notice.run.error);
  // Without a name for the step, «la ejecución falló» would only repeat the title.
  const step = reason?.known ? `${reason.label}. ` : '';
  const body = `${step}${followUpSentence(notice.followUp, notice.retryFailed)} ${OLDER}`;
  const detail = reason?.detail ?? null;

  return {
    title:
      notice.followUp === 'manual' && notice.retryFailed
        ? 'La última ejecución falló dos veces'
        : `La última ejecución falló${when}`,
    body,
    detail,
    offersRelaunch: notice.followUp !== 'auto_retry',
  };
}

/**
 * How the review offers to relaunch the video, or null when it should not offer at all.
 *
 * Only when nothing else is going to relaunch it (`noticeCopy`'s `offersRelaunch`). A failed group
 * run is repeated rather than re-analysed: the replay keeps its group and its batch, so the video
 * counts again in the execution it fell out of, while a re-analysis lands outside both and leaves
 * the member out of that execution's numbers all the same. A run that kept no job cannot be
 * repeated, and upstream would refuse it; a re-analysis is what is left then. Nor is a
 * configuration error: a replay sends the configuration snapshot that failed, while a re-analysis
 * takes the configuration as it is now — fixed — or lets somebody pick another.
 */
export function relaunchOffer(notice: NewerRunNotice | null): 'replay' | 'reanalyze' | null {
  if (!notice || !noticeCopy(notice).offersRelaunch) return null;
  if (
    notice.kind === 'failed' &&
    notice.followUp !== 'fix_configuration' &&
    notice.run.groupId !== null &&
    notice.run.hasStoredJob
  ) {
    return 'replay';
  }

  return 'reanalyze';
}

/**
 * What happens next to a failed run, for a list that shows it among others; null when it did not
 * fail.
 *
 * `retried` when the list already holds its automatic retry: then there is nothing to wait for,
 * and «la reintentará sola» would have somebody waiting for a run that already happened.
 */
export function failedRunFollowUp(
  run: Pick<
    AnalysisRun,
    'status' | 'sourceType' | 'trigger' | 'cancellation' | 'hasStoredJob' | 'finishedAt' | 'error'
  >,
  now: number,
  retried = false,
): string | null {
  if (run.status !== 'FAILED') return null;
  if (retried) return 'Virtual Profiler ya la reintentó.';

  return followUpSentence(failedFollowUp(run, now), run.trigger === 'auto_retry');
}

/** A failed run a link named: the review opens another one and says why this one has nothing. */
export interface NamedFailure {
  notice: Extract<NewerRunNotice, { kind: 'failed' }>;
  /** Its automatic retry is already in the list: nothing to wait for, nothing to relaunch here. */
  retried: boolean;
}

/**
 * Which run a review asks upstream for, given the one its link names.
 *
 * A failed run left no result, and upstream answers a request for it with a 404 the review read as
 * «Ese vídeo ya no existe». Links do name failed runs — a queue row, the hub's Actividad, the
 * selector — so such a link opens what a plain link opens, and keeps the failure for the review to
 * say (`namedFailureCopy`). A run the list does not know is asked for by name: the first page of
 * fifty can leave it out.
 */
export function runToOpen(
  runs: readonly AnalysisRun[] | null | undefined,
  namedRunId: string | undefined,
  now: number,
): { runId: string | undefined; namedFailure: NamedFailure | null } {
  const named = namedRunId === undefined ? undefined : runs?.find((run) => run.id === namedRunId);

  if (namedRunId !== undefined && named?.status !== 'FAILED') {
    return { runId: namedRunId, namedFailure: null };
  }

  const namedFailure: NamedFailure | null = named
    ? {
        notice: {
          kind: 'failed',
          run: named,
          followUp: failedFollowUp(named, now),
          retryFailed: named.trigger === 'auto_retry',
        },
        retried: (runs ?? []).some((run) => run.retryOf === named.id),
      }
    : null;

  return { runId: defaultRunId(runs), namedFailure };
}

/**
 * The review's notice about the failed run its link named.
 *
 * Not `noticeCopy`: that one is about the latest run, and says the review is on an older one —
 * here the failed run may be older than what is on screen. `hasComplete` is whether a run of the
 * video succeeded, which is what is shown instead; otherwise it is upstream's active analysis.
 */
export function namedFailureCopy(
  named: NamedFailure,
  hasComplete: boolean,
  formatWhen: (when: Date) => string = spanishInstant,
): NoticeCopy {
  const { run, followUp, retryFailed } = named.notice;
  const when = run.finishedAt ? ` el ${formatWhen(run.finishedAt)}` : '';
  const reason = runFailureReason(run.error);
  const step = reason?.known ? `${reason.label}. ` : '';
  const next = named.retried
    ? 'Virtual Profiler ya la reintentó.'
    : followUpSentence(followUp, retryFailed);
  const shown = hasComplete
    ? 'No dejó resultado: lo que ves es la última completa.'
    : 'No dejó resultado: lo que ves es el análisis activo del vídeo.';

  return {
    title: `Esta ejecución falló${when}`,
    body: `${step}${next} ${shown}`,
    detail: reason?.detail ?? null,
    offersRelaunch: !named.retried && followUp !== 'auto_retry',
  };
}

/** How the review offers to relaunch the failed run its link named; see `relaunchOffer`. */
export function namedFailureRelaunch(named: NamedFailure): 'replay' | 'reanalyze' | null {
  return named.retried ? null : relaunchOffer(named.notice);
}
